<template>
  <!--
    AB#21807 — one Comms mood face. Drawn, not an emoji, so it matches in light
    and dark: every colour comes from the --mood-N tokens through CSS classes
    (SVG presentation attributes can't read var()).
  -->
  <svg
    class="mf"
    :class="`mf--l${level}`"
    :width="size"
    :height="size"
    viewBox="0 0 28 28"
    role="img"
    :aria-label="label"
    data-test="mood-face"
    :data-level="level"
  >
    <title>{{ label }}</title>
    <circle class="mf-disc" cx="14" cy="14" r="13" />
    <path
      v-if="level === 5"
      class="mf-line"
      d="M9 12.4 Q10.5 10.6 12 12.4 M16 12.4 Q17.5 10.6 19 12.4"
    />
    <template v-else>
      <circle class="mf-eye" cx="10.5" cy="12" :r="level === 0 ? 1.2 : 1.5" />
      <circle class="mf-eye" cx="17.5" cy="12" :r="level === 0 ? 1.2 : 1.5" />
    </template>
    <path v-if="level === 1" class="mf-line mf-brow" d="M8.2 9.2 L11.6 10.6 M19.8 9.2 L16.4 10.6" />
    <path class="mf-line mf-mouth" :d="mouth" />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { MOOD_LABELS, type MoodLevel } from '@/composables/useCommsMood';

const props = withDefaults(defineProps<{ level: MoodLevel; size?: number; title?: string }>(), {
  size: 24,
  title: '',
});

const MOUTHS: Record<MoodLevel, string> = {
  5: 'M9 15.2 Q14 20.4 19 15.2',
  4: 'M9.5 16 Q14 19 18.5 16',
  3: 'M9.5 17 L18.5 17',
  2: 'M9.5 18.3 Q14 15.6 18.5 18.3',
  1: 'M9 19 Q14 13.8 19 19',
  0: 'M10 17 L18 17',
};

const mouth = computed(() => MOUTHS[props.level] ?? MOUTHS[0]);
const label = computed(() => props.title || MOOD_LABELS[props.level] || MOOD_LABELS[0]);
</script>

<style scoped>
.mf {
  flex-shrink: 0;
  display: inline-block;
  vertical-align: middle;
  --mf-c: var(--mood-0);
  --mf-s: var(--mood-0-soft);
}
.mf--l5 {
  --mf-c: var(--mood-5);
  --mf-s: var(--mood-5-soft);
}
.mf--l4 {
  --mf-c: var(--mood-4);
  --mf-s: var(--mood-4-soft);
}
.mf--l3 {
  --mf-c: var(--mood-3);
  --mf-s: var(--mood-3-soft);
}
.mf--l2 {
  --mf-c: var(--mood-2);
  --mf-s: var(--mood-2-soft);
}
.mf--l1 {
  --mf-c: var(--mood-1);
  --mf-s: var(--mood-1-soft);
}
.mf-disc {
  fill: var(--mf-s);
  stroke: var(--mf-c);
  stroke-width: 1.6;
}
.mf-eye {
  fill: var(--mf-c);
}
.mf-line {
  fill: none;
  stroke: var(--mf-c);
  stroke-width: 1.8;
  stroke-linecap: round;
}
.mf-brow {
  stroke-width: 1.6;
}
</style>
